import { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { useI18n } from "@/i18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { NumberInput } from "@/components/common";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";

/** MHD zuordnen / korrigieren. Welcher der drei Fälle vorliegt (Zuordnung, Stammdatenkorrektur,
 *  Nachfolge-Charge), entscheidet ausschließlich der Server über /inventory/lot-bbd/context. */
export default function LotBbdDialog({ open, onClose, onDone, row }) {
  const { t, fmtDate } = useI18n();
  const [ctx, setCtx] = useState(null);
  const [bbd, setBbd] = useState("");
  const [qty, setQty] = useState("");
  const [lotNumber, setLotNumber] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [idemKey, setIdemKey] = useState("");

  useEffect(() => {
    if (!open || !row) return;
    setCtx(null); setBbd(""); setReason("");
    setQty(String(row.quantity ?? ""));
    setLotNumber(row.lot_number || "");
    setIdemKey(`bbd-${row.variant_id}-${row.lot_id || "nolot"}-${row.storage_location_id}-${Date.now()}`);
    api.get("/inventory/lot-bbd/context", { params: { lot_id: row.lot_id || undefined } })
      .then((r) => setCtx(r.data))
      .catch((e) => { toast.error(apiError(e)); onClose(); });
  }, [open, row, onClose]);

  const mode = ctx?.mode;
  const hint = !ctx ? t("common.loading")
    : mode === "in_place" ? t("bbd.hintInPlace")
      : mode === "successor" ? t("bbd.hintSuccessor", { qty: ctx.used_quantity ?? 0 })
        : t("bbd.hintAssign");
  const needsQty = !!ctx && mode !== "in_place";
  const reasonMissing = !!ctx && mode !== "assignment" && !reason.trim();

  const submit = async () => {
    setBusy(true);
    try {
      if (mode === "in_place") {
        await api.patch(`/stock-lots/${row.lot_id}/best-before-date`,
          { best_before_date: bbd, reason });
        toast.success(t("bbd.doneCorrected"));
      } else {
        const res = await api.post("/inventory/lot-bbd/assign", {
          variant_id: row.variant_id, warehouse_id: row.warehouse_id,
          storage_location_id: row.storage_location_id, quality: row.quality,
          lot_id: row.lot_id || null, quantity: Number(qty), best_before_date: bbd,
          lot_number: lotNumber, reason,
          idempotency_key: `${idemKey}-${qty}-${bbd}`,
        });
        toast.success(t("bbd.doneAssigned", { qty: res.data.quantity }));
      }
      onDone?.(); onClose();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="lot-bbd-dialog">
        <DialogHeader><DialogTitle>
          {!ctx || mode === "assignment" ? t("bbd.assign") : t("bbd.correct")} · {row?.product_name || row?.sku}
        </DialogTitle></DialogHeader>
        <p className="text-xs text-muted-foreground" data-testid="lot-bbd-hint">{hint}</p>
        <div className="grid grid-cols-2 gap-x-4 gap-y-1 rounded-md border border-border bg-muted/40 p-3 text-xs"
          data-testid="lot-bbd-context">
          <div>{t("inv.productSku")}: <span className="font-mono">{row?.sku}</span></div>
          <div>{t("ex.supplierLot")}: <span className="font-mono">{row?.lot_number || "—"}</span></div>
          <div>{t("bbd.currentBbd")}: {row?.best_before_date ? fmtDate(row.best_before_date) : "—"}</div>
          <div>{t("ex.location")}: <span className="font-mono">{row?.location_code || "—"}</span></div>
          <div>{t("ex.quality")}: {t(`quality.${row?.quality}`, row?.quality)}</div>
          <div>{t("wo.available")}: <span className="font-mono font-semibold">{row?.quantity}</span></div>
          {mode === "successor" && (
            <div className="col-span-2">{t("bbd.usedQuantity")}: <span className="font-mono">{ctx?.used_quantity}</span></div>)}
        </div>
        <div className="space-y-3">
          <div>
            <Label>{t("bbd.newBbd")}</Label>
            <Input type="date" className="mt-1.5" value={bbd} data-testid="lot-bbd-date"
              onChange={(e) => setBbd(e.target.value)} />
          </div>
          {needsQty && (
            <div>
              <Label>{t("bbd.quantity")}</Label>
              <NumberInput className="mt-1.5" value={qty} onChange={setQty} testId="lot-bbd-qty" />
            </div>)}
          {mode === "assignment" && (
            <div>
              <Label>{t("bbd.lotNumber")}</Label>
              <Input className="mt-1.5" value={lotNumber} data-testid="lot-bbd-lot-number"
                onChange={(e) => setLotNumber(e.target.value)} />
            </div>)}
          <div>
            <Label>{t("bbd.reason")}</Label>
            <Input className="mt-1.5" value={reason} data-testid="lot-bbd-reason"
              onChange={(e) => setReason(e.target.value)} />
            {reasonMissing && <p className="mt-1 text-xs text-destructive" data-testid="lot-bbd-reason-required">{t("bbd.reasonRequired")}</p>}
          </div>
        </div>
        <DialogFooter>
          <Button onClick={submit} data-testid="lot-bbd-submit"
            disabled={busy || !ctx || !bbd || reasonMissing || (needsQty && !(Number(qty) > 0))}>
            {t("common.save")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
